"use client";

import { motion, AnimatePresence } from "motion/react";
import { useState, useMemo, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  MapPin,
  Clock,
  CheckCircle2,
  X,
  CircleQuestionMark,
  ChevronLeft,
  ChevronRight,
  Navigation,
  Plus,
  Minus,
} from "lucide-react";
import {
  decodeHtml,
  generateValueLabelRange,
  formatCurrency,
  cleanHeaderTags,
} from "@/_helper/helper";
import { GiMeal } from "react-icons/gi";
import { CgNotes } from "react-icons/cg";
import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/navigation";
import {
  getHotelByURL,
  insertWebsiteInquiry,
  insertBookingReference,
} from "@/services/hotel.services";
import { ErrorToast } from "../common/Toast";
import Preloader from "../home/Preloader";
import Slider from "react-slick";
import { ISettings } from "@/store/homeSlice";

// Meal plan order priority
const mealPlanOrder = {
  EP: 1,
  CP: 2,
  MAP: 3,
  AP: 4,
};

const mealPlanLabels = {
  EP: "Room Only",
  CP: "Room & Breakfast",
  MAP: "Room, Breakfast & Lunch Or Dinner",
  AP: "Room & All Meals",
};

export default function HotelDetailClient({
  HotelDetails,
}: {
  HotelDetails: any;
}) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const loading = useAppSelector((state) => state.hotel.loading);
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [nav1, setNav1] = useState<Slider | null>(null);
  const [nav2, setNav2] = useState<Slider | null>(null);
  const slider1 = useRef<Slider>(null);
  const slider2 = useRef<Slider>(null);
  const [activeImages, setActiveImages] = useState<any[]>([]);
  const [activeTitle, setActiveTitle] = useState<string>("");
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  useEffect(() => {
    if (selectedIndex !== null) {
      setNav1(slider1.current);
      setNav2(slider2.current);
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setSelectedIndex(null);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "unset";
    }
  }, [selectedIndex, activeImages]);

  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );
  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue;
  const AdultOptions = generateValueLabelRange(1, 10, "string");
  const ChildOptions = generateValueLabelRange(0, 10, "string");

  const formik: any = useFormik({
    initialValues: {
      Initial: "",
      FirstName: "",
      LastName: "",
      EmailID: "",
      MobileNo: "",
      CityID: null,
      CheckIn: "",
      CheckOut: "",
      Nights: null,
      Adults: "1",
      Child: "0",
      HotelID: null,
      Remarks: "",
      InquiryType: "Hotels",
      PackageName: HotelDetails?.HotelName || "",
    },
    validateOnBlur: true,
    validationSchema: Yup.object({
      FirstName: Yup.string().trim().required("First Name is required"),
      MobileNo: Yup.string()
        .required("Mobile number is required")
        .matches(
          /^(?!(\d)\1{9})[6-9]\d{9}$/,
          "Please enter a valid 10-digit mobile number",
        )
        .test(
          "no-sequential-spam",
          "Sequential patterns are not allowed",
          (val) => val !== "1234567890" && val !== "0987654321",
        ),
      EmailID: Yup.string()
        .required("Email is required")
        .test("is-email-required", "Email is not valid", function (value) {
          if (!value) {
            return true;
          }
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          return emailRegex.test(value);
        }),
      CheckIn: Yup.date()
        .required("Check-in date is required")
        .min(
          new Date(new Date().setHours(0, 0, 0, 0)),
          "Check-in date cannot be in the past",
        ),
      CheckOut: Yup.date()
        .required("Check-out date is required")
        .min(Yup.ref("CheckIn"), "Check-out date must be after check-in")
        .test(
          "is-after-checkin",
          "Check-out date must be after check-in",
          function (value, context) {
            const { CheckIn } = context.parent;
            if (!value || !CheckIn) return true;
            return new Date(value).getTime() > new Date(CheckIn).getTime();
          },
        ),
      Adults: Yup.string()
        .required("Adults is required")
        .test(
          "is-greater-than-zero",
          "Atleast 1 Adult is required",
          (value) => Number(value) > 0,
        ),
    }),
    onSubmit: async (
      values,
      {
        // setSubmitting,
        resetForm,
      },
    ) => {
      const payload = {
        FirstName: values?.FirstName ? values?.FirstName : "",
        LastName: "",
        Email: values?.EmailID ? values?.EmailID : "",
        MobileNo: `${values?.MobileNo}`,
        CheckInDate: values?.CheckIn ? values?.CheckIn : null,
        CheckOut: values.CheckOut ? values.CheckOut : null,
        Adults: values?.Adults ? +values?.Adults : 0,
        Child: values?.Child ? +values?.Child : 0,
        HotelID: HotelDetails?.ID ? +HotelDetails?.ID : null,
        InquiryType: values.InquiryType,
        PackageName: values.PackageName,
        WebsiteURL: typeof window !== "undefined" ? window.location.href : "",
      };
      const response = await dispatch(insertWebsiteInquiry(payload));
      if (response.meta.requestStatus === "fulfilled") {
        resetForm();
        router.push("/thankyou/");
      } else {
        ErrorToast("Error sending Inquiry!");
      }
    },
  });

  const hotel = HotelDetails;

  // Extract base price and rate type from the lowest rate
  const basePriceInfo = useMemo(() => {
    let lowestPrice = Infinity;
    let rateType = "";

    hotel?.RoomCategoryList?.forEach((category: any) => {
      category.HotelPrice?.forEach((price: any) => {
        const cost = price.CoupleCost;
        if (cost && cost > 0) {
          if (cost < lowestPrice) {
            lowestPrice = cost;
            rateType = "Per Couple";
          }
        }
      });
    });

    if (lowestPrice !== Infinity) {
      return { price: lowestPrice.toString(), rateType };
    }

    if (!hotel?.WeekDays) return { price: "0", rateType: "" };
    const price = hotel?.WeekDays?.split(",")[0].trim();
    return { price: price || "0", rateType: "" };
  }, [hotel]);

  // Group pricing by RoomCategoryID and then by Meal Plan
  const groupedRoomCategories = useMemo(() => {
    if (!hotel?.RoomCategoryList) return [];

    return hotel.RoomCategoryList.map((category: any) => {
      const mealPlans = (category.HotelPrice || [])
        .filter((price: any) => price && Number(price.CoupleCost) > 0)
        .map((price: any) => {
          const meal = price.MealPlanName || price.Meal || "EP";
          return {
            meal: meal,
            mealPlanID: price.MealPlanID,
            label: mealPlanLabels[meal as keyof typeof mealPlanLabels] || meal,
            price: price.CoupleCost,
            rateType: "Per Couple",
            description: price.MealPlanDescription || price.Description || "",
          };
        })
        .sort((a: any, b: any) => {
          const orderA =
            mealPlanOrder[a.meal as keyof typeof mealPlanOrder] || 99;
          const orderB =
            mealPlanOrder[b.meal as keyof typeof mealPlanOrder] || 99;
          return orderA - orderB;
        });

      return {
        id: category.RoomCategoryID,
        name: category.RoomCategoryName,
        mealPlans: mealPlans,
        roomDetails: category,
      };
    });
  }, [hotel]);

  if (!hotel) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Hotel Not Found
          </h1>
          <Link
            href="/sou-hotels"
            className="text-orange-600 hover:text-orange-700"
          >
            Browse All Hotels
          </Link>
        </div>
      </div>
    );
  }

  // Gallery items - combine BannerImage, MainImage and list from HotelImages
  const galleryImages = useMemo(() => {
    const images: any[] = [];
    if (hotel.HotelImages && Array.isArray(hotel.HotelImages)) {
      hotel.HotelImages.forEach((img: any) => {
        if (img.ImagePath) images.push(img.ImagePath);
      });
    }
    return Array.from(new Set(images.filter(Boolean)));
  }, [hotel]);

  return (
    <>
      {loading && <Preloader />}
      <div className="min-h-screen bg-white">
        {/* Hero Section - Restored to Old Design */}
        <section className="relative h-[60vh] overflow-hidden">
          <Image
            src={hotel.BannerImage || hotel.MainImage || "/placeholder.jpg"}
            alt={hotel.HotelName}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0 pointer-events-none"></div>

          <div className="absolute inset-0 flex items-end pointer-events-none pb-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="pointer-events-auto"
              >
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-2xl">
                  {hotel.HotelName}
                </h1>

                <div className="flex flex-wrap items-center gap-6 text-white">
                  {hotel.HotelCategory && (
                    <div className="inline-block bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                      {hotel.HotelCategory} Star
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-amber-400" />
                    <span className="font-medium text-lg">
                      {hotel.CityName}, {hotel.StateName}
                    </span>
                  </div>

                  {hotel.FarFrom && +hotel.FarFrom > 0 && (
                    <div className="flex items-center gap-2">
                      <Navigation className="w-5 h-5 text-amber-400" />
                      <span className="font-medium text-lg">
                        {hotel.FarFrom} km away from SOU
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Main Content - Restored to Old Design */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-3 space-y-12">
              {/* Room Options - Grouped by Room Category with Meal Plans */}
              {groupedRoomCategories?.length > 0 &&
                groupedRoomCategories?.some(
                  (category: any) => category.mealPlans?.length > 0,
                ) && (
                  <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="space-y-8"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 font-sans tracking-tight border-b-2 border-orange-500 inline-block pb-2">
                      Room Options
                    </h2>

                    <div className="space-y-10">
                      {groupedRoomCategories?.map(
                        (category: any, categoryIndex: number) => {
                          if (category.mealPlans?.length == 0) return;
                          const allImages: string[] = [];
                          if (category.roomDetails?.RoomImage)
                            allImages.push(category.roomDetails.RoomImage);
                          if (category.roomDetails?.MainImage)
                            allImages.push(category.roomDetails.MainImage);
                          if (
                            category.roomDetails?.Images &&
                            Array.isArray(category.roomDetails.Images)
                          ) {
                            category.roomDetails.Images.forEach((img: any) => {
                              const src =
                                img.OtherImage || img.ImagePath || img;
                              if (
                                typeof src === "string" &&
                                src.trim().length > 0
                              ) {
                                allImages.push(src);
                              }
                            });
                          }
                          const roomImages = Array.from(
                            new Set(allImages.filter(Boolean)),
                          );
                          const displayImages =
                            roomImages.length > 0
                              ? roomImages
                              : hotel.MainImage
                                ? [hotel.MainImage]
                                : ["/placeholder.jpg"];

                          return (
                            <motion.div
                              key={category.id}
                              initial={{ opacity: 0, y: 30 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ delay: categoryIndex * 0.1 }}
                              className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
                            >
                              {/* Unified Card Header */}
                              <div className="bg-[#FFF9F2] px-4 md:px-8 py-4 border-b border-orange-100">
                                <h3 className="text-2xl md:text-3xl font-bold text-[#FF6B00]">
                                  {category.name}
                                </h3>
                              </div>

                              <div className="p-4 md:p-8 grid lg:grid-cols-12 gap-8 items-start">
                                {/* Left: Image Grid */}
                                <div className="lg:col-span-5 space-y-4">
                                  {displayImages[0] && (
                                    <div
                                      className="relative rounded-lg overflow-hidden cursor-pointer group shadow-inner aspect-[4/3] md:aspect-square"
                                      onClick={() => {
                                        setActiveImages(displayImages);
                                        setActiveTitle(category.name);
                                        setSelectedIndex(0);
                                      }}
                                    >
                                      <Image
                                        src={
                                          displayImages[0] || "/placeholder.jpg"
                                        }
                                        alt={category.name}
                                        fill
                                        className="object-cover transition-transform duration-700 group-hover:scale-105"
                                      />
                                      <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors"></div>
                                    </div>
                                  )}
                                  {(displayImages[1] || displayImages[2]) && (
                                    <div className="grid grid-cols-2 gap-4">
                                      {displayImages[1] && (
                                        <div
                                          className="relative rounded-xl overflow-hidden cursor-pointer aspect-square group"
                                          onClick={() => {
                                            setActiveImages(displayImages);
                                            setActiveTitle(category.name);
                                            setSelectedIndex(1);
                                          }}
                                        >
                                          <Image
                                            src={
                                              displayImages[1] ||
                                              "/placeholder.jpg"
                                            }
                                            alt={category.name}
                                            fill
                                            className="object-cover transition-transform duration-700 group-hover:scale-110"
                                          />
                                        </div>
                                      )}
                                      {displayImages[2] && (
                                        <div
                                          className="relative rounded-lg overflow-hidden cursor-pointer aspect-square group"
                                          onClick={() => {
                                            setActiveImages(displayImages);
                                            setActiveTitle(category.name);
                                            setSelectedIndex(2);
                                          }}
                                        >
                                          <Image
                                            src={
                                              displayImages[2] ||
                                              "/placeholder.jpg"
                                            }
                                            alt={category.name}
                                            fill
                                            className="object-cover transition-transform duration-700 group-hover:scale-110"
                                          />
                                          {displayImages.length > 3 && (
                                            <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] flex flex-col items-center justify-center text-white">
                                              <span className="text-2xl font-semibold">
                                                +{displayImages.length - 3}
                                              </span>
                                              <span className="text-xs uppercase font-normal tracking-[0.2em] mt-1">
                                                Photos
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Right: Rate Card Listing */}
                                <div className="lg:col-span-7">
                                  <div className="space-y-4">
                                    {category.mealPlans.map(
                                      (meal: any, idx: number) => (
                                        <div
                                          key={idx}
                                          className="bg-white border border-gray-100 rounded-3xl p-4 shadow-sm hover:shadow-md transition-all hover:border-orange-200 group/meal"
                                        >
                                          <div className="flex flex-col md:flex-row justify-between gap-6">
                                            {/* Meal & Description */}
                                            <div className="flex-1 space-y-3">
                                              <div className="flex items-center gap-3 mb-0">
                                                <div className="w-6 h-6 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600 border border-orange-100 group-hover/meal:bg-orange-100 transition-colors">
                                                  <GiMeal className="w-5 h-5" />
                                                </div>
                                                <div>
                                                  <span className="font-semibold text-gray-900 text-lg tracking-tight">
                                                    {meal.meal}
                                                  </span>
                                                </div>
                                              </div>
                                              <p className="text-xs text-gray-600 font-medium leading-relaxed">
                                                {meal.description}
                                              </p>
                                            </div>

                                            {/* Price & Action */}
                                            <div className="flex flex-col md:items-end gap-4 min-w-[180px]">
                                              <div className="flex flex-col md:items-end">
                                                <div className="flex items-baseline gap-1">
                                                  <span className="text-2xl font-semibold text-gray-900">
                                                    {formatCurrency(meal.price)}
                                                  </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <span className="text-xs text-gray-400 font-bold">
                                                    <span className="uppercase">
                                                      + taxes,{" "}
                                                    </span>
                                                    <span className="">
                                                      {meal.rateType} / Night
                                                    </span>
                                                  </span>
                                                </div>
                                              </div>
                                              {hotel.BookingURL ? (
                                                <button
                                                  onClick={async () => {
                                                    // Fire-and-forget: track redirect before opening third-party URL
                                                    insertBookingReference(
                                                      hotel.ID,
                                                      window.location.href,
                                                    ).catch(() => {});
                                                    window.open(
                                                      hotel.BookingURL,
                                                      "_blank",
                                                      "noopener,noreferrer",
                                                    );
                                                  }}
                                                  className="w-full md:w-auto px-4 py-2 bg-gradient-to-r from-[#FF6B00] to-[#FF8C00] text-white text-[11px] font-semibold rounded-2xl hover:shadow-xl hover:shadow-orange-500/40 hover:-translate-y-0.5 transition-all uppercase tracking-widest text-center cursor-pointer"
                                                >
                                                  Book Now
                                                </button>
                                              ) : (
                                                <Link
                                                  href={`/cart?hotelId=${hotel.ID}&roomCategoryId=${category.id}&mealPlanID=${meal.mealPlanID}`}
                                                  className="w-full md:w-auto px-4 py-2 bg-gradient-to-r from-[#FF6B00] to-[#FF8C00] text-white text-[11px] font-semibold rounded-2xl hover:shadow-xl hover:shadow-orange-500/40 hover:-translate-y-0.5 transition-all uppercase tracking-widest text-center"
                                                >
                                                  Book Now
                                                </Link>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Room Features Footer */}
                              {category.roomDetails &&
                                (category.roomDetails.MaxAdults ||
                                  category.roomDetails.RoomSize ||
                                  category.roomDetails.BedType) && (
                                  <div className="bg-[#FFF9F2]/50 px-4 md:px-8 py-5 border-t border-orange-50/50">
                                    <div className="flex flex-wrap gap-8 text-[11px] font-semibold text-gray-400 uppercase tracking-[0.2em]">
                                      {category.roomDetails.MaxAdults && (
                                        <div className="flex items-center gap-3">
                                          <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shadow-sm">
                                            <span className="text-base">
                                              👥
                                            </span>
                                          </div>
                                          <div>
                                            <div className="text-[9px] text-gray-400 mb-0.5">
                                              OCCUPANCY
                                            </div>
                                            <span className="text-gray-900">
                                              {category.roomDetails.MaxAdults}{" "}
                                              ADULTS
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      {category.roomDetails.RoomSize && (
                                        <div className="flex items-center gap-3">
                                          <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shadow-sm">
                                            <span className="text-base">
                                              📏
                                            </span>
                                          </div>
                                          <div>
                                            <div className="text-[9px] text-gray-400 mb-0.5">
                                              ROOM AREA
                                            </div>
                                            <span className="text-gray-900">
                                              {category.roomDetails.RoomSize}{" "}
                                              SQ.FT
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      {category.roomDetails.BedType && (
                                        <div className="flex items-center gap-3">
                                          <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shadow-sm">
                                            <span className="text-base">
                                              🛏️
                                            </span>
                                          </div>
                                          <div>
                                            <div className="text-[9px] text-gray-400 mb-0.5">
                                              BED TYPE
                                            </div>
                                            <span className="text-gray-900">
                                              {category.roomDetails.BedType}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                            </motion.div>
                          );
                        },
                      )}
                    </div>
                  </motion.section>
                )}

              {/* About This Property */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  About This Property
                </h2>
                <div
                  className="text-lg text-gray-700 leading-relaxed prose prose-slate max-w-none blog-content"
                  dangerouslySetInnerHTML={{
                    __html: cleanHeaderTags(
                      decodeHtml(
                        hotel.HotelDescription || hotel.ShortDescription || "",
                      ),
                    ),
                  }}
                />
              </motion.section>

              {/* Photo Gallery - Restored to Old Design */}
              {galleryImages.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100"
                >
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-1.5 h-8 bg-orange-500 rounded-full"></div>
                    <h2 className="text-xl md:text-3xl font-bold text-gray-900 m-0">
                      Photo Gallery
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {galleryImages.map((image, index) => (
                      <div
                        key={index}
                        className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer"
                        onClick={() => {
                          setActiveImages(galleryImages);
                          setActiveTitle("Photo Gallery");
                          setSelectedIndex(index);
                        }}
                      >
                        <Image
                          src={image}
                          alt={`${hotel.HotelName} ${index + 1}`}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                          <span className="text-white font-semibold">
                            View Image
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Hotel Highlights (Notes) - Restored to Old Design */}
              {hotel.Notes && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-white shadow-lg border border-gray-100  rounded-2xl md:rounded-3xl p-4 md:p-8"
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <CgNotes className="w-6 h-6 text-green-600" />
                    Notes
                  </h2>
                  <div
                    className="notes-list text-gray-700 leading-relaxed font-sans blog-content"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(decodeHtml(hotel.Notes) || ""),
                    }}
                  />
                </motion.section>
              )}

              {/* Policies - Restored to Old Design */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-blue-50 shadow-lg border border-blue-100 rounded-2xl md:rounded-3xl p-4 md:p-8"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-blue-600" />
                  Hotel Policies
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      Check-in Time
                    </h3>
                    <p className="text-gray-700">{hotel.CheckInTime}</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      Check-out Time
                    </h3>
                    <p className="text-gray-700">{hotel.CheckOutTime}</p>
                  </div>
                  {hotel.CancellationPolicy && (
                    <div className="md:col-span-2">
                      <h3 className="font-bold text-gray-900 mb-2">
                        Cancellation Policy
                      </h3>
                      <div
                        className="text-gray-700 cancellation-list blog-content"
                        dangerouslySetInnerHTML={{
                          __html: cleanHeaderTags(
                            decodeHtml(hotel.CancellationPolicy) || "",
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
              </motion.section>

              {/* FAQ Section */}
              {HotelDetails?.FAQList && HotelDetails?.FAQList?.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border-orange-100"
                >
                  <div className="flex items-center gap-3 mb-6 md:mb-8">
                    <div className="w-1.5 h-6 md:h-8 bg-orange-500 rounded-full"></div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 m-0">
                      FAQ
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {HotelDetails?.FAQList?.map((faq: any, index: number) => {
                      const isOpen = openFaqIndex === index;
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                            isOpen
                              ? "bg-white border-orange-200 shadow-md ring-1 ring-orange-500/10"
                              : "bg-white/50 border-gray-100 hover:border-orange-200"
                          }`}
                        >
                          <button
                            onClick={() =>
                              setOpenFaqIndex(isOpen ? null : index)
                            }
                            className="w-full px-4 md:px-6 py-4 md:py-5 flex items-center justify-between text-left group"
                          >
                            <div className="flex items-center gap-3 md:gap-4">
                              <div
                                className={`flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-colors font-bold text-xs md:text-sm ${
                                  isOpen
                                    ? "bg-orange-500 text-white"
                                    : "bg-orange-100 text-orange-600 group-hover:bg-orange-500 group-hover:text-white"
                                }`}
                              >
                                {index + 1}
                              </div>
                              <h3
                                className={`text-base md:text-lg font-bold transition-colors ${
                                  isOpen
                                    ? "text-orange-600"
                                    : "text-gray-900 group-hover:text-orange-600"
                                }`}
                              >
                                {faq.Question}
                              </h3>
                            </div>
                            <div
                              className={`p-1.5 rounded-full transition-all duration-300 ${
                                isOpen
                                  ? "bg-orange-100"
                                  : "bg-gray-100 group-hover:bg-orange-100"
                              }`}
                            >
                              {isOpen ? (
                                <Minus className="w-4 h-4 text-orange-600 transition-colors" />
                              ) : (
                                <Plus className="w-4 h-4 text-gray-600 group-hover:text-orange-600 transition-colors" />
                              )}
                            </div>
                          </button>
                          <AnimatePresence>
                            {isOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  duration: 0.3,
                                  ease: "easeInOut",
                                }}
                              >
                                <div className="px-4 md:px-6 pb-5 md:pb-6 pt-0 ml-10 md:ml-12">
                                  <div
                                    className="text-gray-600 leading-relaxed text-sm md:text-base blog-content border-t border-orange-50 border-dashed pt-4"
                                    dangerouslySetInnerHTML={{
                                      __html: cleanHeaderTags(
                                        decodeHtml(faq.Answer),
                                      ),
                                    }}
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.section>
              )}
            </div>

            {/* Right Column - Booking Card Restored to Old Design */}
            {false && (
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="sticky top-24 bg-white rounded-2xl shadow-2xl p-6 border-2 border-orange-100"
                >
                  <div className="mb-6">
                    <div className="text-sm text-gray-600 mb-2 font-semibold">
                      Starting from
                    </div>
                    {+basePriceInfo.price > 0 ? (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-4xl font-bold text-gray-900 tracking-tight">
                            {formatCurrency(basePriceInfo.price)}
                          </span>
                          {/* <span className="text-gray-600 font-medium">/ night</span> */}
                        </div>
                        {basePriceInfo.rateType && (
                          <div className="text-sm text-gray-500 font-medium mt-1 uppercase tracking-wider">
                            {basePriceInfo.rateType} / night
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm ">
                        For Rate Call us on :
                        <span className="block text-2xl mt-1 font-bold text-orange-600">
                          <Link
                            href={`tel:${PhoneNo}`}
                            className="hover:underline"
                          >
                            {PhoneNo}
                          </Link>
                        </span>
                      </div>
                    )}
                  </div>

                  <form
                    className="space-y-4"
                    onSubmit={(e) => formik.handleSubmit(e)}
                  >
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                        Full Name
                      </label>
                      <input
                        name="FirstName"
                        type="text"
                        placeholder="Enter Your Full Name*"
                        maxLength={50}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values?.FirstName}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                      />
                      {formik.touched.FirstName && formik.errors.FirstName && (
                        <div className="text-red-500 text-xs mt-1">
                          {formik.errors.FirstName}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                        Email ID
                      </label>
                      <input
                        id="EmailID"
                        name="EmailID"
                        type="email"
                        placeholder="Enter Your Email*"
                        maxLength={50}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values?.EmailID}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                      />
                      {formik.touched.EmailID && formik.errors.EmailID && (
                        <div className="text-red-500 text-xs mt-1">
                          {formik.errors.EmailID}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                        Mobile No
                      </label>
                      <input
                        id="MobileNo"
                        name="MobileNo"
                        type="number"
                        placeholder="Enter Your Mobile No.*"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values?.MobileNo}
                        onInput={(e: any) => {
                          return (e.target.value = Math.max(
                            0,
                            parseInt(e.target.value),
                          )
                            .toString()
                            .slice(0, 10));
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                      />
                      {formik.touched.MobileNo && formik.errors.MobileNo && (
                        <div className="text-red-500 text-xs mt-1">
                          {formik.errors.MobileNo}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                        Check-in Date
                      </label>
                      <input
                        id="CheckIn"
                        name="CheckIn"
                        type="date"
                        placeholder="Enter Your Email*"
                        maxLength={50}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values?.CheckIn}
                        onClick={(e) => (e.currentTarget as any).showPicker?.()}
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium cursor-pointer"
                      />
                      {formik.touched.CheckIn && formik.errors.CheckIn && (
                        <div className="text-red-500 text-xs mt-1">
                          {formik.errors.CheckIn}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                        Check-out Date
                      </label>
                      <input
                        id="CheckOut"
                        name="CheckOut"
                        type="date"
                        placeholder="Enter Your Email*"
                        maxLength={50}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values?.CheckOut}
                        onClick={(e) => (e.currentTarget as any).showPicker?.()}
                        min={
                          formik.values?.CheckIn
                            ? new Date(
                                new Date(formik.values?.CheckIn).getTime() +
                                  86400000,
                              )
                                .toISOString()
                                .split("T")[0]
                            : new Date().toISOString().split("T")[0]
                        }
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium cursor-pointer"
                      />
                      {formik.touched.CheckOut && formik.errors.CheckOut && (
                        <div className="text-red-500 text-xs mt-1">
                          {formik.errors.CheckOut}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                          Adults
                        </label>
                        <select
                          name="Adults"
                          id="Adults"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values?.Adults}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                        >
                          {AdultOptions.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}{" "}
                              {+item.value > 1 ? "Adults" : "Adult"}
                            </option>
                          ))}
                        </select>
                        {formik.touched.Adults && formik.errors.Adults && (
                          <div className="text-red-500 text-xs mt-1">
                            {formik.errors.Adults}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                          Child
                        </label>
                        <select
                          name="Child"
                          id="Child"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values?.Child}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                        >
                          {ChildOptions.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label} {+item.value > 0 && "Child"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      onClick={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                      }}
                      className="w-full py-4 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-[1.02] text-center block shadow-lg cursor-pointer"
                    >
                      Check Availability
                    </button>
                  </form>

                  <div className="mt-6 space-y-3 text-sm font-medium">
                    <div className="flex items-center gap-2 text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Best price guarantee</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Instant confirmation</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Free cancellation available</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </div>

        {/* Image Modal with Synced Slider */}
        <AnimatePresence>
          {selectedIndex !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-xl flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex justify-between items-center px-4 sm:px-8 py-4 sm:py-6 text-white absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 via-black/30 to-transparent">
                <div className="flex items-center gap-4 sm:gap-6">
                  <div className="border-l-4 border-orange-500 pl-4 sm:pl-6">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span>{hotel.HotelName}</span>
                      {activeTitle && (
                        <span className="text-orange-500 text-sm sm:text-lg font-semibold">
                          • {activeTitle}
                        </span>
                      )}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedIndex(null)}
                  className="bg-white/10 hover:bg-orange-600 p-2.5 sm:p-3 rounded-full backdrop-blur-lg transition-all group border border-white/10 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="text-white group-hover:rotate-90 transition-transform duration-500 w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Sliders Area */}
              <div className="flex-1 flex flex-col mt-20 justify-center items-center p-4 md:p-8 w-full max-w-[1400px] mx-auto overflow-hidden">
                {/* Main Slider */}
                <div className="w-full relative px-6 md:px-20 mb-8 max-h-[70vh]">
                  <Slider
                    asNavFor={nav2!}
                    ref={slider1}
                    initialSlide={selectedIndex}
                    infinite={true}
                    speed={500}
                    slidesToShow={1}
                    slidesToScroll={1}
                    arrows={true}
                    nextArrow={<NextArrow />}
                    prevArrow={<PrevArrow />}
                    fade={true}
                    className="w-full gallery-main-slider"
                  >
                    {activeImages.map((image, index) => (
                      <div
                        key={index}
                        className="relative outline-none flex justify-center"
                      >
                        <div className="relative w-full h-[65vh] md:h-[70vh]">
                          <Image
                            src={image}
                            alt="Hotel View"
                            fill
                            className="object-contain"
                            priority
                            sizes="100vw"
                          />
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>

                {/* Thumbnails Slider */}
                <div className="w-full max-w-xl px-6 h-20 md:h-24">
                  <Slider
                    asNavFor={nav1!}
                    ref={slider2}
                    initialSlide={selectedIndex}
                    slidesToShow={
                      activeImages.length < 5 ? activeImages.length : 5
                    }
                    swipeToSlide={true}
                    focusOnSelect={true}
                    infinite={activeImages.length > 1}
                    centerMode={true}
                    centerPadding="0px"
                    variableWidth={true}
                    arrows={false}
                    className="thumb-nav-slider"
                    responsive={[
                      { breakpoint: 1024, settings: { slidesToShow: 5 } },
                      { breakpoint: 768, settings: { slidesToShow: 4 } },
                      { breakpoint: 480, settings: { slidesToShow: 3 } },
                    ]}
                  >
                    {activeImages.map((image, index) => (
                      <div
                        key={index}
                        className="px-2 py-1 focus:outline-none cursor-pointer group !w-auto"
                      >
                        <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 border-transparent transition-all opacity-30 [.slick-current_&]:opacity-100 [.slick-current_&]:border-orange-500 [.slick-current_&]:scale-110 group-hover:opacity-70 group-hover:scale-105 shadow-sm">
                          <Image
                            src={image}
                            alt="Thumbnail"
                            fill
                            className="object-cover"
                            sizes="100px"
                          />
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

// Custom Navigation Arrows for Slick Slider
function NextArrow({ onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="absolute right-[-20px] md:right-[-40px] top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-orange-600 p-3 rounded-full text-white backdrop-blur-xl transition-all border border-white/10 flex items-center justify-center group"
    >
      <ChevronRight
        size={30}
        className="group-hover:translate-x-1 transition-transform"
      />
    </button>
  );
}

function PrevArrow({ onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="absolute left-[-20px] md:left-[-40px] top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-orange-600 p-3 rounded-full text-white backdrop-blur-xl transition-all border border-white/10 flex items-center justify-center group"
    >
      <ChevronLeft
        size={30}
        className="group-hover:-translate-x-1 transition-transform"
      />
    </button>
  );
}
